import type { CityConfig } from "../city-template/types";

export const coronaConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Corona",
  state: "California",
  stateAbbr: "CA",
  slug: "corona",
  route: "/corona",
  title: "Emergency Passport Corona, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Corona, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Riverside County and Inland Empire travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Corona, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Corona and Inland Empire travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Corona%20California%20cityscape%20at%20golden%20hour%20with%20the%20Santa%20Ana%20Mountains%20and%20Cleveland%20National%20Forest%20in%20the%20background%2C%20suburban%20neighborhoods%20and%20commercial%20developments%20along%20the%2091%20freeway%20corridor%2C%20palm%20trees%20and%20manicured%20landscaping%2C%20warm%20sunset%20light%20casting%20long%20shadows%20over%20the%20Inland%20Empire%2C%20professional%20cityscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20modern%20suburban%20environment%20with%20mountain%20backdrop%20and%20amber%20sky&width=1800&height=700&seq=corona-hero-001&orientation=landscape",
  heroImageAlt: "Corona California cityscape at golden hour with Santa Ana Mountains backdrop",
  geoPosition: "33.8753;-117.5664",
  geoPlacename: "Corona, California",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "11000 Wilshire Blvd Suite 1000, Los Angeles, CA 90024",
    distance: "Located in West LA — approximately 55–70 minutes from Corona via CA-91 and I-5",
  },
  nearbyCitiesZips: [
    { name: "Corona", zips: "92877, 92878, 92879, 92880, 92881, 92882, 92883" },
    { name: "Norco", zips: "92860" },
    { name: "Eastvale", zips: "92880" },
    { name: "Riverside", zips: "92501–92509, 92512–92519, 92521" },
    { name: "Chino", zips: "91708, 91710" },
    { name: "Chino Hills", zips: "91709" },
    { name: "Ontario", zips: "91761, 91762, 91764" },
    { name: "Rancho Cucamonga", zips: "91701, 91730, 91737, 91739" },
    { name: "Anaheim Hills", zips: "92807, 92808" },
    { name: "Yorba Linda", zips: "92885, 92886, 92887" },
    { name: "Lake Elsinore", zips: "92530, 92532" },
    { name: "Murrieta", zips: "92562, 92563, 92564" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Inland Empire departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Riverside", route: "/riverside" },
    { name: "Ontario", route: "/ontario" },
    { name: "Anaheim", route: "/anaheim" },
    { name: "Los Angeles", route: "/los-angeles" },
    { name: "Pasadena", route: "/pasadena" },
    { name: "Santa Ana", route: "/santa-ana" },
    { name: "Long Beach", route: "/long-beach" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Corona remotely via secure courier. We do not have a walk-in office in Corona — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Inland Empire travelers.",
  fpcOfficeAddress: "200 Spectrum Center Dr, Irvine, CA 92618",
  fpcOfficeDistance: "Serving all of western Riverside County and the Inland Empire",
  serviceAreaIntro: "From the Dos Lagos shopping district to the Eagle Glen area, from the Corona foothills to the 91 freeway corridor — Fast Passport Center serves the entire Corona community. We regularly help travelers from Norco, Eastvale, Chino, and Riverside get their passports fast.",
  nearbyCitiesIntro: "We serve all Corona neighborhoods and nearby Inland Empire cities including Norco, Eastvale, Riverside, Chino, Chino Hills, and Ontario. If you are in western Riverside County or the Inland Empire, we have you covered.",
  howItWorksIntro: "Most Corona travelers complete everything online and ship documents overnight. No need to drive to Los Angeles or wait in line at the regional agency.",
  mistakesIntro: "With Ontario International Airport (ONT) just minutes away and John Wayne Airport (SNA) accessible via CA-91, Corona travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Corona and Inland Empire travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Corona? Here are the most common ones we hear from Inland Empire travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Inland Empire international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Anthony R.",
      location: "Corona, CA",
      rating: 5,
      text: "My flight to Sydney was in 2 days and my passport had expired. Fast Passport Center got me a new one in 36 hours. Saved my business trip to Australia.",
      service: "Emergency Passport",
    },
    {
      name: "Patricia L.",
      location: "Norco, CA",
      rating: 5,
      text: "Needed to renew for a family vacation to Hawaii. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Kevin M.",
      location: "Eastvale, CA",
      rating: 5,
      text: "Lost my passport the week before my honeymoon to Greece. They walked me through the DS-64 process and got me a replacement in 48 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Nancy H.",
      location: "Chino Hills, CA",
      rating: 5,
      text: "As a frequent international traveler from the Inland Empire, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "cor-1",
      name: "Corona Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "415 E 6th St",
      city: "Corona",
      state: "CA",
      zip: "92879",
      phone: "(951) 371-8161",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Corona",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "cor-2",
      name: "Norco Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2810 Hamner Ave",
      city: "Norco",
      state: "CA",
      zip: "92860",
      phone: "(951) 735-9511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.5 miles from George Ingalls Equestrian Event Center",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "cor-3",
      name: "Riverside Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "4150 Chicago Ave",
      city: "Riverside",
      state: "CA",
      zip: "92507",
      phone: "(951) 682-0461",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "5 miles from Corona",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Riverside County. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "cor-4",
      name: "Chino Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "5495 Philadelphia St",
      city: "Chino",
      state: "CA",
      zip: "91710",
      phone: "(909) 627-4341",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "6 miles from Corona",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for northwestern Riverside County residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "cor-5",
      name: "Ontario Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "430 N Palo Verde St",
      city: "Ontario",
      state: "CA",
      zip: "91762",
      phone: "(909) 983-4671",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "8 miles from Corona",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for northern Riverside County residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "cor-6",
      name: "Riverside County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "2724 Gateway Dr",
      city: "Riverside",
      state: "CA",
      zip: "92507",
      phone: "(951) 486-7000",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "5 miles from Corona",
      website: "https://www.rivcoacr.org/",
      websiteLabel: "Riverside County Clerk",
      notes: "County facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown LA", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
    { feature: "91 Corridor Coverage", agency: "No 91 freeway corridor expertise", fpc: "91 freeway corridor, Dos Lagos, and Inland Empire business travel expertise" },
    { feature: "Multi-Person Processing", agency: "Separate appointments per person", fpc: "Handle families and groups in parallel" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Inland Empire photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Corona area acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Corona applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the LA regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Corona families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Corona travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Corona." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Corona address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Corona before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Corona for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Inland Empire travelers." },
    { q: "How fast can I renew my passport in Corona?", a: "Fast Passport Center offers fast passport renewal in Corona in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Corona in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Corona?", a: "Same-day passport appointments are only available at the Los Angeles Passport Agency (11000 Wilshire Blvd Suite 1000, Los Angeles, CA 90024) for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Corona residents." },
    { q: "Can I expedite a child passport in Corona?", a: "Yes. Fast Passport Center expedites child passports in Corona for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Corona with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Corona?", a: "With Fast Passport Center's emergency courier service, Corona travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to drive to the Los Angeles Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Corona?", a: "Popular options include the Corona Post Office (92879), Norco Post Office (92860), Riverside Main Post Office (92507), and Riverside County Clerk. All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Corona?", a: "Same-day passports are only available at the LA Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option." },
    { q: "What if I live in Norco, Eastvale, or Chino Hills?", a: "We serve all of western Riverside County and nearby areas including Norco, Eastvale, Chino, Chino Hills, and Yorba Linda. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Corona?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
    { q: "Do you help with passport photos in Corona?", a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Corona, Norco, and Riverside offer passport photo services. We review your photos before submission to ensure they meet State Department standards." },
    { q: "What documents do I need for a first-time passport in Corona?", a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk." },
    { q: "Can you help with a child's passport in Corona?", a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Corona families through the entire process and help schedule acceptance clerk appointments." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Corona", address: "550 N Main St, Corona, CA 92878", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Norco", address: "2800 Hamner Ave, Norco, CA 92860", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Corona", address: "1262 E Ontario Ave, Corona, CA 92881", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Eastvale", address: "12481 Limonite Ave, Eastvale, CA 91752", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Corona", address: "479 N McKinley St, Corona, CA 92879", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Riverside", address: "3700 Tyler St, Riverside, CA 92503", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Chino Hills", address: "4200 Chino Hills Pkwy, Chino Hills, CA 91709", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Corona", address: "480 N McKinley St, Corona, CA 92879", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Corona",
      address: "10244 Indiana Ave, Riverside, CA 92503",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Riverside",
      address: "6280 Brockton Ave, Riverside, CA 92506",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Ontario",
      address: "1101 E Holt Blvd, Ontario, CA 91761",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Norco",
      address: "3201 Horseless Carriage Dr, Norco, CA 92860",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for western Riverside County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "ONT",
      name: "Ontario International Airport",
      distance: "12 miles from Corona",
      address: "1923 E Avion St, Ontario, CA 91761",
      note: "The closest major airport to Corona. International flights to Mexico and connections to Asia and Europe.",
      flightUrl: "https://www.flyontario.com/",
      flightLabel: "Ontario Airport",
      featured: true,
    },
    {
      code: "SNA",
      name: "John Wayne Airport (Orange County)",
      distance: "25 miles from Corona",
      address: "18601 Airport Way, Santa Ana, CA 92707",
      note: "Alternative airport for Orange County. International flights to Mexico, Canada, and domestic connections.",
      flightUrl: "https://www.ocair.com/",
      flightLabel: "John Wayne Airport",
      featured: false,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "50 miles from Corona",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via CA-91 and I-5.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
  ],
  vitalRecordsLinks: [{ icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from California CDPH Vital Records for Riverside County.", url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx" }, { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through California CDPH Vital Records.", url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx" }, { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Riverside County Clerk-Recorder.", url: "https://www.rivcoacr.org/" }, { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through Riverside County Superior Court.", url: "https://www.riverside.courts.ca.gov/" }],
};